import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Button, Chip, LinearProgress,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import { api } from '../api/client';
import type { Subscription } from '../api/types';
import { PageHeader, fmtUnits } from '../components/common';

const STATUS: Record<Subscription['status'], { label: string; color: 'info' | 'warning' | 'success' | 'error' }> = {
  OPEN: { label: '모집중', color: 'info' },
  CLOSED: { label: '마감', color: 'warning' },
  ISSUED: { label: '발행완료', color: 'success' },
  SETTLED: { label: '정산완료', color: 'success' },
  CANCELLED: { label: '취소(환불)', color: 'error' },
};

export function Subscriptions() {
  const nav = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['subscriptions'],
    queryFn: () => api.get<Subscription[]>('/subscriptions'),
  });

  return (
    <Box>
      <PageHeader crumb="서비스 사용자 · 청약" title="청약 공고" desc="진행 중인 토큰증권 청약을 확인하고 신청합니다. (공고를 클릭하면 상세 정보를 볼 수 있습니다)" />
      {isLoading && <LinearProgress />}
      {error && <Typography color="error">불러오기 실패: {(error as Error).message}</Typography>}
      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>토큰</TableCell>
              <TableCell align="right">단가</TableCell>
              <TableCell align="right">총 수량</TableCell>
              <TableCell>상태</TableCell>
              <TableCell>마감</TableCell>
              <TableCell align="right">상세</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(data ?? []).map((s) => (
              <TableRow key={s.id} hover
                onClick={() => nav(`/subscriptions/${s.id}`)}
                sx={{ cursor: 'pointer' }}>
                <TableCell>
                  <b>{s.tokenName}</b>{' '}
                  <Typography component="span" variant="caption" color="text.secondary">{s.symbol}</Typography>
                </TableCell>
                <TableCell align="right">{fmtUnits(s.unitPrice, 0)}</TableCell>
                <TableCell align="right">{fmtUnits(s.totalAmount, 0)}</TableCell>
                <TableCell>
                  <Chip size="small" color={STATUS[s.status].color} label={STATUS[s.status].label} sx={{ fontWeight: 700 }} />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                    {new Date(s.periodEnd).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined"
                    onClick={(e) => { e.stopPropagation(); nav(`/subscriptions/${s.id}`); }}>상세 보기</Button>
                </TableCell>
              </TableRow>
            ))}
            {data?.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>등록된 공고가 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}
